import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflows } from 'features/libra/ticket/libraTicketsApi'
import type { FC } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'

type ProgressKanbanProps = {
  tickets: ReadonlyArray<LibraTicket>
}

type Stage = 1 | 2 | 3 | 4

interface StageData {
  stage: Stage
  label: string
  tickets: number
  color: string
}

/**
 * Maps a ticket's progress to one of 4 stages
 * @param currentStep - current step index (0-based)
 * @param totalSteps - total number of steps
 * @param isComplete - whether workflow is complete
 * @returns stage number (1-4)
 */
function getStageFromProgress(
  currentStep: number,
  totalSteps: number,
  isComplete: boolean
): Stage {
  // Handle edge cases
  if (isComplete) return 4
  if (totalSteps <= 0) return 1
  if (currentStep < 0) return 1

  // Calculate progress ratio (0 to 1)
  const progressRatio = currentStep / totalSteps

  // Map to 4 stages
  // 0-24.99% → Stage 1
  // 25-49.99% → Stage 2
  // 50-74.99% → Stage 3
  // 75-99.99% → Stage 4
  // 100% → Stage 4 (handled above)
  if (progressRatio < 0.25) return 1
  if (progressRatio < 0.5) return 2
  if (progressRatio < 0.75) return 3
  return 4
}

export const ProgressKanban: FC<ProgressKanbanProps> = ({ tickets }) => {
  // Get workflow IDs from tickets
  const workflowIds = useMemo(() => {
    return Array.from(
      new Set(
        tickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      )
    )
  }, [tickets])

  const { libraWorkflows } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((w) => [w.id, w])),
    [libraWorkflows]
  )

  // Group tickets by stage
  const stageData = useMemo<ReadonlyArray<StageData>>(() => {
    const stages: Array<StageData> = [
      { stage: 1, label: 'Starting', tickets: 0, color: '#ff4d4f' },
      { stage: 2, label: 'Early Progress', tickets: 0, color: '#faad14' },
      { stage: 3, label: 'Mid Progress', tickets: 0, color: '#ffd666' },
      { stage: 4, label: 'Near Complete', tickets: 0, color: '#52c41a' },
    ]

    tickets.forEach((ticket) => {
      const workflow = workflowMap.get(ticket.workflowId)
      if (!workflow) return

      const currentIndex = workflow.stepIndices.get(ticket.stepId) ?? -1
      const totalSteps = workflow.steps.length

      const stage = getStageFromProgress(
        currentIndex,
        totalSteps,
        ticket.workflowComplete
      )

      stages[stage - 1].tickets++
    })

    return stages
  }, [tickets, workflowMap])

  const totalTickets = tickets.length

  return (
    <Container>
      <KanbanBoard>
        {stageData.map((stage) => {
          const percentage =
            totalTickets > 0
              ? Math.round((stage.tickets / totalTickets) * 100)
              : 0

          return (
            <StageColumn key={stage.stage}>
              <StageHeader>
                <StageLabel>{stage.label}</StageLabel>
                <StageCount $color={stage.color}>
                  {stage.tickets}
                  {percentage > 0 && (
                    <StagePercentage> ({percentage}%)</StagePercentage>
                  )}
                </StageCount>
              </StageHeader>
              <StageBar>
                <StageProgress
                  $color={stage.color}
                  $width={percentage}
                  $isEmpty={stage.tickets === 0}
                />
              </StageBar>
            </StageColumn>
          )
        })}
      </KanbanBoard>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.gray[800]};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  padding: 1rem;
`

const KanbanBoard = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
`

const StageColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const StageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const StageLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  text-align: center;
`

const StageCount = styled.div<{ $color: string }>`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ $color }) => $color};
  text-align: center;
  line-height: 1;
`

const StagePercentage = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0.8;
`

const StageBar = styled.div`
  height: 8px;
  background: ${({ theme }) => theme.colors.gray[700]};
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`

const StageProgress = styled.div<{
  $color: string
  $width: number
  $isEmpty: boolean
}>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color, $isEmpty }) => ($isEmpty ? 'transparent' : $color)};
  border-radius: 4px;
  transition: width 0.3s ease;
`
